import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  IntegrationsJoinPremiumPostingItemFragment,
  IntegrationsJoinPremiumPostingsCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

// TODO: recover this when we have local filtering
// import useLocalStorage from 'modules/core/lib/useLocalStorage'
import {
  getDatesRange,
  getPremiumPostingStatusColor,
  getPremiumPostingStatusLabel,
} from 'modules/ats/lib/utils/premiumPortals'

import { PremiumPostingSidepanel } from './PremiumPostingSidepanel'

interface Props {
  atsJobPostingId: number
  loading: boolean
}

// TODO: recover this when we have local filtering

// interface PremiumPostingsTableFilter {
//   statuses: IntegrationsJoinPremiumPostingStatusesEnum[] | undefined
// }

export const PremiumPortalsDataCollection = ({ atsJobPostingId, loading }: Props) => {
  const { access, company } = useCurrentSession()

  // TODO: recover this when we have local filtering
  // const [savedFilters, setSavedFilters] = useLocalStorage<PremiumPostingsTableFilter | undefined>(
  //   'atsPremiumPostingsTableFilter'
  // )
  const [selectedPremiumPosting, setSelectedPremiumPosting] = useState<
    IntegrationsJoinPremiumPostingItemFragment | undefined
  >(undefined)

  const source = useGraphqlDataCollectionSource({
    document: IntegrationsJoinPremiumPostingsCollectionDocument,
    mapData: (data) => data.integrationsJoin.premiumPostingsConnection.nodes,
    mapPagination: (data) => ({
      total: data.integrationsJoin.premiumPostingsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings }) => {
      // TODO: recover this when we have local filtering
      // const statuses = filters.status && filters.status.length > 0 ? filters.status : undefined

      return {
        atsJobPostingId,
        accessId: access.id,
        companyId: company?.id,
        sort: sortings?.[0],
      }
    },
    defaultSortings: {
      field: 'fromDate',
      order: 'desc',
    },
    filters: {},
    presets: [],
    // TODO: recover this when we have local filtering
    // currentFilters: { status: savedFilters?.statuses || DEFAULT_FILTERS },
    // filters: {
    //   status: {
    //     label: i18n.t('ats.job_details.premium_portals.common.status'),
    //     type: 'in',
    //     options: {
    //       options: IntegrationsJoinPremiumPostingStatusesEnumValues.map(
    //         (status: IntegrationsJoinPremiumPostingStatusesEnum) => ({
    //           label: premiumPostingStatusLabels[status],
    //           value: status,
    //         })
    //       ),
    //     },
    //   },
    // },
    // presets: [
    //   {
    //     label: i18n.t('ats.job_details.premium_portals.filters.active'),
    //     filter: {
    //       status: ['posted', 'pending', 'processing'],
    //     },
    //   },
    //   {
    //     label: i18n.t('ats.job_details.premium_portals.filters.unposted'),
    //     filter: { status: ['unposted'] },
    //   },
    //   {
    //     label: i18n.t('ats.job_details.premium_portals.filters.expired'),
    //     filter: { status: ['expired'] },
    //   },
    //   {
    //     label: i18n.t('ats.job_details.premium_portals.filters.archived'),
    //     filter: { status: ['archived'] },
    //   },
    // ],
    sortings: {
      fromDate: {
        label: i18n.t('ats.job_details.premium_portals.common.start_date'),
        sortBy: 'fromDate',
      },
      toDate: {
        label: i18n.t('ats.job_details.premium_portals.common.end_date'),
        sortBy: 'toDate',
      },
      productName: {
        label: i18n.t('ats.job_details.premium_portals.common.product'),
        sortBy: 'productName',
      },
      status: {
        label: i18n.t('ats.job_details.premium_portals.common.status'),
        sortBy: 'status',
      },
    },
    grouping: {
      collapsible: true,
      groupBy: {
        'jobBoard.name': {
          name: i18n.t('ats.job_details.premium_portals.common.portal'),
          label(groupId) {
            return String(groupId)
          },
        },
        status: {
          name: i18n.t('ats.job_details.premium_portals.common.status'),
          label(groupId) {
            return String(groupId)
          },
        },
      },
    },
    itemOnClick: (item) => () => setSelectedPremiumPosting(item),
  })

  if (loading) {
    return null
  }

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  label: i18n.t('ats.job_details.premium_portals.table.headers.portal'),
                  render: ({ jobBoard }) => ({
                    type: 'company',
                    value: {
                      name: jobBoard.name,
                      src: jobBoard.logoUrl,
                    },
                  }),
                },
                {
                  label: i18n.t('ats.job_details.premium_portals.table.headers.product'),
                  render: ({ product }) => product.name || 'Premium Product',
                  sorting: 'productName',
                },
                {
                  label: i18n.t('ats.job_details.premium_portals.table.headers.product_type'),
                  render: ({ integrationJoinOrder }) =>
                    integrationJoinOrder.premiumOfferType == 'bundle'
                      ? integrationJoinOrder.premiumOfferName
                      : i18n.t('ats.job_details.premium_portals.sidepanel.individual_product'),
                },
                {
                  label: i18n.t('ats.job_details.premium_portals.table.headers.duration'),
                  render: ({ product }) =>
                    i18n.t('ats.job_details.marketplace.common.duration_days', {
                      duration: product.durationDays,
                    }),
                },
                {
                  label: i18n.t('ats.job_details.premium_portals.table.headers.date_range'),
                  sorting: 'fromDate',
                  render: ({ fromDate, toDate }) => getDatesRange(fromDate, toDate),
                },
                {
                  label: i18n.t('ats.job_details.premium_portals.table.headers.status'),
                  sorting: 'status',
                  render: ({ status }) => ({
                    type: 'status',
                    value: {
                      status: getPremiumPostingStatusColor(status),
                      label: getPremiumPostingStatusLabel(status),
                    },
                  }),
                },
              ],
            },
          },
        ]}
      />
      {selectedPremiumPosting && (
        <PremiumPostingSidepanel
          posting={selectedPremiumPosting}
          onClose={() => setSelectedPremiumPosting(undefined)}
        />
      )}
    </>
  )
}
